import React, { useState } from "react";

const TreatmentModal = ({ isOpen, onClose, onSave, parents = [] }) => {
  const [selectedParents, setSelectedParents] = useState([]);
  const [billingParents, setBillingParents] = useState([]);
  const [noParentAttended, setNoParentAttended] = useState(false);
  const [manualParentName, setManualParentName] = useState("");
  const [useManualEntry, setUseManualEntry] = useState(false);
  const [doNotSplit, setDoNotSplit] = useState(false);

  // Validation: At least one attendance option must be selected
  const isAttendanceSelected = noParentAttended ||
                                 (useManualEntry && manualParentName.trim()) ||
                                 selectedParents.length > 0;

  const handleSave = () => {
    const attendanceData = {
      selectedParents: selectedParents || [],
      billingParents: doNotSplit ? [] : billingParents || [],
      noParentAttended,
      manualParentName: useManualEntry ? manualParentName.trim() : ""
    };
    onSave(attendanceData.selectedParents, attendanceData.billingParents, attendanceData);
    onClose();
  };

  const handleSkip = () => {
    onSave([], [], { selectedParents: [], billingParents: [], noParentAttended: false, manualParentName: "" });
    onClose();
  };

  const handleNoParentAttendedChange = (checked) => {
    setNoParentAttended(checked);
    if (checked) {
      setSelectedParents([]);
      setBillingParents([]);
      setUseManualEntry(false);
      setManualParentName("");
    }
  };

  const handleManualEntryChange = (checked) => {
    setUseManualEntry(checked);
    if (checked) {
      setSelectedParents([]);
      setBillingParents([]);
      setNoParentAttended(false);
    } else {
      setManualParentName("");
      setBillingParents([]);
    }
  };

  const handleDoNotSplitChange = (checked) => {
    setDoNotSplit(checked);
    if (checked) {
      setBillingParents([]);
    }
  };

  return isOpen ? (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
      <div className="bg-white rounded-sm shadow-lg w-full max-w-3xl p-6">
        {/* Modal Header */}
        <div className="flex justify-between items-center border-b pb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Attendance and Split Billing
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        {/* Attendance & Split Billing Controls */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          {/* Attendance Checkbox Group */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Select Attendance
            </h3>
            <div className="border rounded-sm p-3 bg-gray-50 space-y-3">
              {/* No Parent Attended Option */}
              <label className="flex items-center space-x-2 pb-2 border-b border-gray-300">
                <input
                  type="checkbox"
                  checked={noParentAttended}
                  onChange={(e) => handleNoParentAttendedChange(e.target.checked)}
                  className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-sm font-semibold text-red-600">
                  No Parent Attended
                </span>
              </label>

              {/* Manual Entry Option */}
              <div className="pb-2 border-b border-gray-300">
                <label className="flex items-center space-x-2 mb-2">
                  <input
                    type="checkbox"
                    checked={useManualEntry}
                    onChange={(e) => handleManualEntryChange(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm font-semibold text-gray-900">
                    Other Parent/Guardian (Manual Entry)
                  </span>
                </label>
                {useManualEntry && (
                  <input
                    type="text"
                    value={manualParentName}
                    onChange={(e) => setManualParentName(e.target.value)}
                    placeholder="Enter parent/guardian name"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                  />
                )}
              </div>

              {/* Linked Parents */}
              {parents && parents.length > 0 ? (
                parents.map((parent) => (
                  <label
                    key={parent._id}
                    className="flex items-center space-x-2 mb-1">
                    <input
                      type="checkbox"
                      checked={selectedParents.includes(parent)}
                      onChange={() =>
                        setSelectedParents((prev) =>
                          prev.includes(parent)
                            ? prev.filter((p) => p !== parent)
                            : [...prev, parent]
                        )
                      }
                      disabled={noParentAttended || useManualEntry}
                      className={`h-4 w-4 ${
                        noParentAttended || useManualEntry
                          ? "cursor-not-allowed opacity-50"
                          : "text-indigo-600 border-gray-300 focus:ring-indigo-500"
                      } rounded`}
                    />
                    <span className={`text-sm ${
                      noParentAttended || useManualEntry
                        ? "text-gray-400"
                        : "text-gray-900"
                    }`}>
                      {parent.firstName} {parent.lastName}
                    </span>
                  </label>
                ))
              ) : (
                <div className="text-sm text-gray-900">No linked parents found</div>
              )}
            </div>
          </div>

          {/* Split Billing Checkbox Group */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Select Split Billing
            </h3>
            <div className="border rounded-sm p-3 bg-gray-50 space-y-3">
              {/* Do Not Split Option */}
              <label className="flex items-center space-x-2 pb-2 border-b border-gray-300">
                <input
                  type="checkbox"
                  checked={doNotSplit}
                  onChange={(e) => handleDoNotSplitChange(e.target.checked)}
                  disabled={noParentAttended}
                  className={`h-4 w-4 ${
                    noParentAttended
                      ? "cursor-not-allowed opacity-50"
                      : "text-gray-600 border-gray-300 rounded focus:ring-gray-500"
                  }`}
                />
                <span className={`text-sm font-semibold ${
                  noParentAttended ? "text-gray-400" : "text-gray-900"
                }`}>
                  Do Not Split
                </span>
              </label>

              {/* Manual Entry Parent Option (if manual entry is selected and has name) */}
              {useManualEntry && manualParentName.trim() && (
                <label className="flex items-center space-x-2 pb-2 border-b border-gray-300">
                  <input
                    type="checkbox"
                    checked={billingParents.some(p => p.manualEntry)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setBillingParents([{ manualEntry: true, firstName: manualParentName.trim(), lastName: "" }]);
                      } else {
                        setBillingParents([]);
                      }
                    }}
                    disabled={doNotSplit}
                    className={`h-4 w-4 ${
                      doNotSplit
                        ? "cursor-not-allowed opacity-50"
                        : "text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    } rounded`}
                  />
                  <span className={`text-sm ${
                    doNotSplit ? "text-gray-400" : "text-gray-900"
                  }`}>
                    {manualParentName.trim()}
                  </span>
                </label>
              )}

              {/* Linked Parents */}
              {parents && parents.length > 0 ? (
                parents.map((parent) => (
                  <label
                    key={parent._id}
                    className="flex items-center space-x-2 mb-1">
                    <input
                      type="checkbox"
                      checked={billingParents.includes(parent)}
                      onChange={() =>
                        setBillingParents((prev) =>
                          prev.includes(parent)
                            ? prev.filter((p) => p !== parent)
                            : [...prev, parent]
                        )
                      }
                      disabled={!selectedParents.includes(parent) || noParentAttended || useManualEntry || doNotSplit}
                      className={`h-4 w-4 ${
                        selectedParents.includes(parent) && !noParentAttended && !useManualEntry && !doNotSplit
                          ? "text-indigo-600 border-gray-300 focus:ring-indigo-500"
                          : "cursor-not-allowed opacity-50"
                      } rounded`}
                    />
                    <span
                      className={`text-sm ${
                        selectedParents.includes(parent) && !noParentAttended && !useManualEntry && !doNotSplit
                          ? "text-gray-900"
                          : "text-gray-400"
                      }`}>
                      {parent.firstName} {parent.lastName}
                    </span>
                  </label>
                ))
              ) : (
                <div className="text-sm text-gray-900">No linked parents found</div>
              )}
            </div>
          </div>
        </div>

        {/* Warning Box */}
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-300 rounded-sm">
          <p className="text-sm text-yellow-800">
            <strong>⚠️ Warning:</strong> Parent/Guardian must be present for at least 40% of session to split
          </p>
        </div>

        {/* Summary Section */}
        <div className="mt-6">
          <h3 className="text-base font-semibold text-gray-900">Summary</h3>
          <p className="mt-2 text-sm text-gray-700">
            <strong>Attendance:</strong>{" "}
            {noParentAttended
              ? "No Parent Attended"
              : useManualEntry && manualParentName.trim()
              ? manualParentName.trim()
              : selectedParents.length > 0
              ? selectedParents
                  .map((p) => `${p.firstName} ${p.lastName}`)
                  .join(", ")
              : "None"}
          </p>
          <p className="mt-1 text-sm text-gray-700">
            <strong>Split Billing With:</strong>{" "}
            {doNotSplit
              ? "Do Not Split"
              : billingParents.length > 0
              ? billingParents
                  .map((p) => `${p.firstName} ${p.lastName}`.trim())
                  .join(", ")
              : "None"}
          </p>
        </div>

        {/* Modal Actions */}
        <div className="flex justify-end space-x-2 mt-6">
          <button
            className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-200 rounded-sm hover:bg-gray-300"
            onClick={onClose}>
            Cancel
          </button>
          <button
            className={`px-3 py-2 text-sm font-semibold text-white rounded-sm ${
              isAttendanceSelected
                ? "bg-indigo-600 hover:bg-indigo-500"
                : "bg-gray-400 cursor-not-allowed"
            }`}
            onClick={handleSave}
            disabled={!isAttendanceSelected}>
            Save
          </button>
        </div>
      </div>
    </div>
  ) : null;
};

export default TreatmentModal;
